import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Select from 'src/components/common/gridWrapper/Grid_Select';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { CommonCodeColumn, CompCdColumn } from 'src/utils/grid/GridColumnContent';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { formatDate, formatDateWithTime } from 'src/utils/string/stringUtil';

import { PRD4060_R01Dto, defaultPRD4060_R01Dto } from './Dto_PRD4060_R01';
import { usePRD4060_R01Store } from './Store_PRD4060_R01';
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { cellDateComparator } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'PRD4060_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<PRD4060_R01Dto> | null>;
}

export const Table_PRD4060_R01 = (props: Props) => {
	const { gridRef } = props;

	const {
		PRD4060_R01List,
		selectedPRD4060_R01List, setSelectedPRD4060_R01List, initSelectedPRD4060_R01List,
		columnState, setColumnState,
		scrollTop, setScrollTop,
	} = usePRD4060_R01Store();

	const {
		downtimeValueList,
		jobTeamList,
		compCdList,
	} = useCommonCodeStore();

	const getCompositeKey = useCallback((row: PRD4060_R01Dto) =>
		`${row.productResultNo}|${row.downtimeNo}`,
	[]);

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const {
		settingRowIndex,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: PRD4060_R01List || [],
		defaultGridData: defaultPRD4060_R01Dto as PRD4060_R01Dto,
		selectedRow: selectedPRD4060_R01List,
	});

	const getRowIdForGrid = useCallback((params: { data?: PRD4060_R01Dto; id?: string }) => {
		const id = params.data?.valueIndex || params.id;
		return String(id);
	}, []);

	const headerNames: Record<string, string> = {
		rowIndex: '',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'productResultNo')(params), []);

	const baseColumnDefs: ColDef<PRD4060_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '생산실적번호', colId: 'productResultNo', field: 'productResultNo', width: 150,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'productResultNo'),
		},
		{
			headerName: '사업장', colId: 'compCd', field: 'compCd', width: 100,
			cellStyle: (p) => getCellStyle(p, 'compCd'),
			...CompCdColumn.options(compCdList, false, undefined, undefined),
		},
		{
			headerName: '작업일자', colId: 'productDt', field: 'productDt', width: 100,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'productDt'),
			valueFormatter: (p: { data?: PRD4060_R01Dto }) =>
				p.data?.productDt ? formatDate(new Date(p.data.productDt)) : '',
			comparator: cellDateComparator,
		},
		{
			headerName: '작업팀', colId: 'jobTeam', field: 'jobTeam', width: 100,
			cellStyle: (p) => getCellStyle(p, 'jobTeam'),
			...CommonCodeColumn.options(jobTeamList, false, undefined, undefined),
		},
		{
			headerName: '작업자', colId: 'productEmp', field: 'productEmp', width: 100,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'workEmp'),
		},
		{
			headerName: '작업자명', colId: 'productEmpNm', field: 'productEmpNm', width: 100,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'workEmpNm'),
		},
		{
			headerName: '시작시간', colId: 'fromTime', field: 'fromTime', width: 150,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'fromTime'),
			valueFormatter: (p: { data?: PRD4060_R01Dto }) =>
				p.data?.fromTime ? formatDateWithTime(p.data.fromTime) : '',
		},
		{
			headerName: '종료시간', colId: 'toTime', field: 'toTime', width: 150,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'toTime'),
			valueFormatter: (p: { data?: PRD4060_R01Dto }) =>
				p.data?.toTime ? formatDateWithTime(p.data.toTime) : '',
		},
		{
			headerName: '사유', colId: 'downtimeCase', field: 'downtimeCase', width: 100,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'remark'),
			...CommonCodeColumn.options(downtimeValueList, true, undefined, undefined),
		},
	], [getCellStyle, compCdList, jobTeamList, downtimeValueList, cellDateComparator]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<PRD4060_R01Dto>[] = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<PRD4060_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const getRowStyleForPUR2020 = useCallback((params: { node: { rowPinned?: string | null } }) => {
		if (params.node.rowPinned === 'bottom') {
			return { background: '#F0F0F0', fontWeight: 'bold', borderTop: 'none' };
		}
		return undefined;
	}, []);

	const shiftRangeSelectionConfig = useMemo(() => ({
		enableShiftRangeSelect: true,
		selectedIds: selectedPRD4060_R01List as Array<string | number>,
		setSelectedIds: (ids: Array<string | number>) => setSelectedPRD4060_R01List(ids.map(String)),
		resolveId: (row: PRD4060_R01Dto) => getCompositeKey(row),
	}), [selectedPRD4060_R01List, setSelectedPRD4060_R01List, getCompositeKey]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		productResultNo: `${PRD4060_R01List.length.toLocaleString()}건`,
		editable: false,
	}]), [PRD4060_R01List]);

	useEffect(() => { ensureSelectedRow(); }, [PRD4060_R01List, selectedPRD4060_R01List]);

	const rowClassRules = useMemo(() => ({
		selected: (params: { data?: PRD4060_R01Dto }) => {
			if (!params.data) return false;
			return selectedPRD4060_R01List.includes(getCompositeKey(params.data));
		},
	}), [selectedPRD4060_R01List, getCompositeKey]);

	const handleRowDragEnd = useCallback((e: any) => {
		const api = gridRef.current?.api || e?.api;
		if (!api) return;
		if (api.applyColumnState) {
			api.applyColumnState({ defaultState: { sort: null as any, sortIndex: null as any } });
		}
	}, [gridRef]);

	return (
		<div className="flex flex-col gap-3 h-full min-h-0">
			<GridContextWrapper<PRD4060_R01Dto>
				enableCellCalculation
				gridRef={gridRef}
				paddedGridData={PRD4060_R01List}
				initSelected={initSelectedPRD4060_R01List}
				defaultRowData={defaultPRD4060_R01Dto}
				baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
				defaultColumnState={defaultColumnState}
				setColumnState={setColumnState}
				localStorageKey={COLUMN_STATE_KEY}
				menuItems={undefined}
			>
				<Grid_Select<PRD4060_R01Dto>
					ref={gridRef}
					rowData={PRD4060_R01List}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					getRowId={getRowIdForGrid}
					onCellKeyDown={undefined}
					setHoveredRowIndex={setHoveredRowIndex}
					columnStateKey={COLUMN_STATE_KEY}
					setColumnState={setColumnState}
					onGridReady={(params) => {
						params.api.getDisplayedRowCount();
						params.api.getRenderedNodes();
					}}
					shiftRangeSelection={shiftRangeSelectionConfig}
					pinnedBottomRowData={pinnedBottomRow}
					rowClassRules={rowClassRules}
					getRowStyle={getRowStyleForPUR2020}
					rowDragManaged={true}
					rowDragEntireRow={true}
					onRowDragEnd={handleRowDragEnd}
					isRowSelectable={(node) => !!node.data}
					suppressRowClickSelection={false}
					ensureDomOrder={true}
					scrollTop={scrollTop}
					setScrollTop={setScrollTop}
				/>
			</GridContextWrapper>
		</div>
	);
};
